import useSWR from 'swr';
import { useSearchParams } from 'next/navigation';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { getEcState } from '@packages/shared/src/utils/ecState/ecState';
import {
  EMP_CLIENT_INFO_COOKIE,
  isAppChannel,
} from '@packages/shared/src/utils/parseEmpClientInfo/parseEmpClientInfo';
import { getEcStateFromParams, getMainAudience } from '../../../utils/audiences';
import type { PromotionVariantsData } from '../types';
import { promotionBannerDataFetcher } from './fetch/promotionBannerDataFetcher';
import { usePreviewSettings } from '../../../components/Preview/usePreviewSettings';
import { previewSettingsParamKeys } from '../../../components/Preview/previewSettingsStore';

export const usePromotionBannerData = (
  fallbackPromotionData: PromotionVariantsData | undefined = undefined,
  shopId?: string,
) => {
  const { language, defaultLanguage } = useI18n();
  const { getCookies } = useCookies();
  const baseQuery = Object.fromEntries(useSearchParams()?.entries() ?? []);
  const { isActive: isPreviewMode, settings: previewSettings } = usePreviewSettings();

  /**
   * Strip preview-managed params (audiences, testDate, showAll) from the URL query so the
   * previewSettingsStore is the single source of truth. testDate is re-added from the store
   * when in preview mode so the fetcher uses the correct preview date.
   */
  const urlQuery = Object.fromEntries(
    Object.entries(baseQuery).filter(
      ([key]) => !(previewSettingsParamKeys as string[]).includes(key),
    ),
  );
  const isApp = isAppChannel(getCookies()[EMP_CLIENT_INFO_COOKIE]);
  const query = {
    ...(!urlQuery.platform && isApp && { platform: 'app' as const }),
    ...urlQuery,
    ...(isPreviewMode && previewSettings.testDate && { testDate: previewSettings.testDate }),
  };
  const ecState =
    previewSettings.audiences && isPreviewMode
      ? getEcStateFromParams(previewSettings.audiences)
      : getEcState(getCookies());

  const platform = query.platform ?? 'web';

  const finalLanguage = language || defaultLanguage || 'de';
  const audience = getMainAudience(ecState);
  const previewTestDate = isPreviewMode ? previewSettings.testDate : undefined;
  const promotionBannerCacheKey = [
    'promotionBannerData',
    finalLanguage,
    audience,
    platform,
    previewTestDate,
  ]
    .filter(Boolean)
    .join('-');
  const { data, isLoading } = useSWR<PromotionVariantsData>(
    promotionBannerCacheKey,
    () =>
      promotionBannerDataFetcher({
        query,
        cookies: getCookies(),
        locale: finalLanguage,
        callOwnApi: true,
        audience,
        shopId,
      }),
    {
      fallbackData: fallbackPromotionData,
      revalidateIfStale: false,
      // INSPIRE-3409 - do not revalidate on focus to apply correct data when getting back to the shop-browser-tab
      revalidateOnFocus: false,
    },
  );
  return {
    data,
    isLoading,
    endpoint: promotionBannerCacheKey,
    hasExpiredData: [data?.active, data?.selected].some((promoData) => promoData?.isExpired),
  };
};
